/**
 * Phone vs declared residence country — mirrors backend PRD 4.29.1 rules.
 */
import { countries, getCountryByCode } from '../data/countries.js';

const SHARED_DIAL_ISO = new Set(['US', 'CA']);

/** Longest dial codes first so +221 matches before +22, etc. */
const countriesByDialLength = [...countries].sort(
  (a, b) => b.phoneCode.length - a.phoneCode.length,
);

export function normalizePhoneE164(telephone) {
  const cleaned = String(telephone || '').replace(/[\s\-()]/g, '');
  if (!cleaned) return '';
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
}

/**
 * Infer residence country from an E.164 phone number (longest matching dial code).
 * For shared +1 (US/CA), returns the first listed country (US).
 * @returns {{ code: string, name: string, phoneCode: string, flag: string } | null}
 */
export function getCountryFromPhoneNumber(telephone) {
  const e164 = normalizePhoneE164(telephone);
  if (!e164 || e164.length < 4) return null;

  for (const country of countriesByDialLength) {
    if (e164.startsWith(country.phoneCode)) {
      return country;
    }
  }
  return null;
}

/**
 * Form defaults for country + cleared localité when prefilling from invitation phone.
 */
export function getResidenceDefaultsFromPhone(telephone) {
  const country = getCountryFromPhoneNumber(telephone);
  if (!country) return {};

  return {
    country: country.name,
    country_code: country.code,
    region_id: '',
    department_id: '',
    commune_id: '',
    state: '',
    city: '',
    postal_code: '',
    address_line: '',
  };
}

export function isPhoneAlignedWithCountry(telephone, countryCode) {
  if (!telephone || !countryCode) return true;
  const country = getCountryByCode(countryCode);
  const e164 = normalizePhoneE164(telephone);
  if (!e164 || e164.length < 4) return true;

  const dial = country.phoneCode.replace('+', '');
  if (dial === '1') {
    return e164.startsWith('+1') && SHARED_DIAL_ISO.has(country.code);
  }
  return e164.startsWith(`+${dial}`);
}

export function isStrictPhoneCountryAlignment(userType) {
  return userType === 'relais' || userType === 'beneficiaire';
}

export function requiresPhoneCountryMismatchAck(userType) {
  return userType === 'parrain_grand' || userType === 'parrain_sympa';
}

export function getPhoneCountryMismatchMessage(countryCode) {
  const country = getCountryByCode(countryCode);
  return (
    `L'indicatif de votre numéro ne correspond pas au pays sélectionné (${country.name}). ` +
    `Utilisez un numéro ${country.phoneCode} ou changez le pays de résidence.`
  );
}
